import type { AuthorizationView } from "../../../server/modules/homecare/careplans";
import type { ScreenContext } from "../../shell/types";
import { api, el } from "../../ui/dom";
import { chip } from "../../ui/record";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { pageHead } from "../empty";

// Home Care › Authorizations (brief 1C-3, mockup homecare/auth): every patient's authorizations,
// each covering one service with its number and dates, as count, frequency or continuous coverage:
// Patient, Client, Service, Certificate, Valid, Shape, Used and the flags (Low balance at the
// watch's %, Due in). Chips filter them (Current, Low balance, Ending soon, Ended, Archived) and a
// search finds a patient, client, number or service. A row opens the patient.

type Row = AuthorizationView & { patient: { id: string; name: string }; client: string };
interface Answer extends PageInfo {
  show: string;
  counts: Record<"current" | "low" | "ending" | "ended" | "archived", number>;
  watch: { usedPct: number; endDays: number };
  authorizations: Row[];
}

const FILTERS: [keyof Answer["counts"], string][] = [
  ["current", "Current"],
  ["low", "Low balance"],
  ["ending", "Ending soon"],
  ["ended", "Ended"],
  ["archived", "Archived"],
];

export async function authorizationsScreen(host: HTMLElement, ctx: ScreenContext) {
  host.append(
    pageHead(ctx, "Each authorization covers one service with a certificate number and dates.", []),
  );
  let show: keyof Answer["counts"] = "current";
  const listId = "homecare/auth";
  // Brief 1E-32: the server answers one page at a time; the chip over it goes with each page.
  const ask = (params: URLSearchParams) => {
    params.set("show", show);
    return api<Answer>("GET", `/api/authorizations?${params.toString()}`);
  };
  const chips = el("div", { class: "stagef", role: "group", "aria-label": "Filter" });
  const res = await ask(firstPageParams(listId, ctx.me.user.id));
  if (!res.ok) {
    host.append(
      el("p", { class: "note", role: "alert" }, "The authorizations couldn't be loaded."),
    );
    return;
  }
  let counts = res.body.counts;
  const drawChips = () =>
    chips.replaceChildren(
      ...FILTERS.map(([k, l]) => {
        const b = el(
          "button",
          {
            type: "button",
            class: `chipbtn${show === k ? " on" : ""}`,
            "aria-pressed": String(show === k),
          },
          `${l} ${counts[k]}`,
        );
        b.addEventListener("click", () => {
          show = k;
          drawChips();
          void rows.reload(true);
        });
        return b;
      }),
    );
  const columns: Column<Row>[] = [
    {
      id: "patient",
      label: "Patient",
      text: (r) => r.patient.name,
      cell: (r) => el("b", {}, r.patient.name),
      search: true,
    },
    { id: "client", label: "Client", text: (r) => r.client, search: true },
    { id: "service", label: "Service", text: (r) => r.service, search: true },
    { id: "number", label: "Certificate", text: (r) => r.number ?? "", search: true },
    { id: "dates", label: "Valid", text: (r) => r.dates },
    { id: "shape", label: "Shape", text: (r) => r.shape },
    {
      id: "used",
      label: "Used",
      num: true,
      text: (r) => (r.of !== null ? `${r.used} / ${r.of}` : String(r.used)),
    },
    {
      id: "flags",
      label: "Flags",
      text: (r) => (r.archived ? `Archived ${r.archived.on}` : r.due),
      cell: (r) =>
        el(
          "span",
          {},
          r.low ? chip("Low balance", "warn") : "",
          r.low ? " " : "",
          r.over ? chip("Over the units", "bad") : "",
          r.over ? " " : "",
          r.archived ? chip(`Archived ${r.archived.on}`, "plain") : chip(r.due, r.dueLevel),
        ),
    },
  ];
  const rows = list<Row>({
    id: listId,
    userId: ctx.me.user.id,
    noun: "authorizations",
    searchLabel: "Search patients, clients, numbers or services",
    first: { rows: res.body.authorizations, page: res.body },
    source: async (params) => {
      const r = await ask(params);
      if (!r.ok) return null;
      counts = r.body.counts;
      drawChips();
      return { rows: r.body.authorizations, page: r.body };
    },
    rowId: (r) => r.id,
    columns,
    onOpen: (r) => ctx.go(`/homecare/patients/${r.patient.id}`),
    empty: "No authorizations yet. They're added on each patient, under Patient and payer.",
  });
  drawChips();
  host.append(chips, rows);
}
